import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Divider,
  Select
} from '@mantine/core';
import { AlertCircle, Check, CirclePlus, Trash, X } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import AppText from '../../common/AppText';
import * as classes from '../../../styles/nestedStyles.module.css';
import { getVariantOptions } from '../../../helpers/webstoreHelper';
import WsColorSwatch from '../webstore/blocks/variantPicker/WsColorSwatch';
import PrintDesignEditorVariantSelectModal from './PrintDesignEditorVariantSelectModal';
import { singularPluralFormat } from '../../../helpers/format';
import ConfirmModal from '../../common/ConfirmModal';

const getSelectedDesignColors = ({
  selectedVariantDesign,
  colorOption,
  productDesign,
  ecomExternalProduct
}) => {
  if (!colorOption?.values?.length) {
    return [];
  }

  const variants = ecomExternalProduct.ecomVendorProductVariants;

  const variantHasColor = (variantId, colorValue) => {
    const variant = variants.find((v) => v.uuid === variantId);

    return variant?.ecomVendorProductVariantOptions.some(
      (option) =>
        option.name === colorOption.name && option.value === colorValue
    );
  };

  const colorExistsInVariantIds = (variantIds, colorValue) =>
    variantIds.some((variantId) => variantHasColor(variantId, colorValue));

  return colorOption.values.filter((color) => {
    const colorValue = color.value;

    // Main design = colors NOT used by any variant design or removed from main design
    if (selectedVariantDesign?.isMainDesign) {
      const isUsedByOtherDesign = productDesign?.variantDesigns.some(
        (variantDesign) =>
          colorExistsInVariantIds(variantDesign.variantIds, colorValue)
      );

      const isRemovedFromMainDesign = variants
        .filter((v) => variantHasColor(v.uuid, colorValue))
        .every((v) => productDesign?.removedVariantIds?.includes(v.uuid));

      return !isUsedByOtherDesign && !isRemovedFromMainDesign;
    }

    // Non-main design = colors used by this design
    return colorExistsInVariantIds(
      selectedVariantDesign?.variantIds || [],
      colorValue
    );
  });
};

const PrintDesignEditorVariantDesignControls = ({
  ecomExternalProduct,
  printTemplate,
  formState,
  printTemplateState,
  setFormState,
  canvasController,
  loading
}) => {
  const [variantControlsState, setVariantControlsState] = useState({
    showVariantRemove: false,
    showVariantSelect: false,
    selectedVariantDesign: null
  });
  const { changeBackgroundColor } = canvasController;

  const isVisible =
    formState.showVariantSelect && !!ecomExternalProduct && !!printTemplate;

  const variantOptions = getVariantOptions(
    ecomExternalProduct?.ecomVendorProductVariants || [],
    { beginningColor: 'white' }
  );
  const colorOption = variantOptions.find(
    (op) => op.name.toLowerCase() === 'color'
  );
  const allVariantsHaveColorsSet = colorOption?.values.every(
    (v) => v.primaryColor
  );

  const productDesign = formState.productDesigns.find(
    (d) => d.id === printTemplateState.id
  );
  const mainDesign = productDesign?.variantDesigns.find(
    (vd) => vd.isMainDesign
  );
  const selectedVariantDesign = productDesign?.variantDesigns?.find(
    (vd) => vd.id === printTemplateState.variantDesignId
  );

  const selectedDesignColors = getSelectedDesignColors({
    selectedVariantDesign,
    colorOption,
    productDesign,
    ecomExternalProduct
  });

  const removedColorOptions =
    (selectedVariantDesign?.isMainDesign &&
      colorOption?.values.filter(
        (color) =>
          !selectedDesignColors.includes(color) &&
          !productDesign.variantDesigns.some((vd) => {
            const colorOptionValuesForDesign = getSelectedDesignColors({
              selectedVariantDesign: vd,
              colorOption,
              productDesign,
              ecomExternalProduct
            });

            return colorOptionValuesForDesign.some(
              (c) => c.value === color.value
            );
          })
      )) ||
    [];

  useEffect(() => {
    if (!loading) {
      setFormState((c) => ({
        ...c,
        selectedColor: selectedDesignColors[0]
      }));
      if (
        selectedDesignColors[0] &&
        !formState.disableTemplateBackgroundColor
      ) {
        changeBackgroundColor(selectedDesignColors[0].primaryColor || '#FFF');
      }
    }
  }, [printTemplateState.variantDesignId, loading]);

  const onAddVariantDesign = (variantIds) => {
    const newVariantDesign = {
      ...mainDesign,
      id: `variant-${Date.now()}`,
      variantIds,
      isMainDesign: false
    };
    setFormState((c) => {
      const updatedProductDesigns = c.productDesigns.map((pd) => {
        if (pd.id === productDesign.id) {
          return {
            ...pd,
            hasChanges: true,
            removedVariantIds: [...(pd.removedVariantIds || [])].filter(
              (v) => !variantIds.includes(v)
            ),
            variantDesigns: [...(pd.variantDesigns || []), newVariantDesign]
          };
        }
        return pd;
      });
      return {
        ...c,
        selectedVariantDesignId: newVariantDesign.id,
        productDesigns: updatedProductDesigns
      };
    });
  };

  const onSelectDesignVariants = (variantIds) => {
    if (selectedVariantDesign.isMainDesign) {
      setFormState((c) => {
        const updatedProductDesigns = c.productDesigns.map((pd) => {
          if (pd.id === productDesign.id) {
            const removedVariantIds = ecomExternalProduct.ecomVendorProductVariants
              .flatMap((v) => v.uuid)
              .filter(
                (uuid) =>
                  !variantIds.includes(uuid) &&
                  !pd.variantDesigns.some((vd) => vd.variantIds.includes(uuid))
              );

            return {
              ...pd,
              hasChanges: true,
              removedVariantIds
            };
          }
          return pd;
        });
        return {
          ...c,
          productDesigns: updatedProductDesigns
        };
      });

      return;
    }

    setFormState((c) => {
      const updatedProductDesigns = c.productDesigns.map((pd) => {
        if (pd.id === productDesign.id) {
          return {
            ...pd,
            hasChanges: true,
            removedVariantIds: [...(pd.removedVariantIds || [])].filter(
              (v) => !variantIds.includes(v)
            ),
            variantDesigns: pd.variantDesigns.map((vd) => {
              if (vd.id === variantControlsState.selectedVariantDesign.id) {
                return {
                  ...vd,
                  variantIds
                };
              }
              return vd;
            })
          };
        }
        return pd;
      });
      return {
        ...c,
        productDesigns: updatedProductDesigns
      };
    });
  };

  const onRemoveVariantDesign = (variantDesignId) => {
    setFormState((c) => {
      const updatedProductDesigns = c.productDesigns.map((pd) => {
        if (pd.id === productDesign.id) {
          return {
            ...pd,
            hasChanges: true,
            variantDesigns: pd.variantDesigns.filter(
              (vd) => vd.id !== variantDesignId
            )
          };
        }
        return pd;
      });
      return {
        ...c,
        productDesigns: updatedProductDesigns,
        selectedVariantDesignId:
          c.selectedVariantDesignId === variantDesignId
            ? mainDesign?.id || null
            : c.selectedVariantDesignId
      };
    });
  };

  const onClearRemovedVariants = () => {
    setFormState((c) => {
      const updatedProductDesigns = c.productDesigns.map((pd) => {
        if (pd.id === productDesign.id) {
          return {
            ...pd,
            hasChanges: true,
            removedVariantIds: []
          };
        }
        return pd;
      });
      return {
        ...c,
        productDesigns: updatedProductDesigns
      };
    });
  };

  return (
    isVisible && (
      <AppStack
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          position: 'absolute',
          overflow: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <AppStack
          style={{
            left: 0,
            top: 65,
            width: 325,
            maxHeight: '100%',
            pointerEvents: 'all',
            display: 'flex',
            position: 'absolute'
          }}
        >
          <AppCard
            radius={4}
            shadow="md"
            style={{
              width: '100%',
              height: '100%',
              padding: 0,
              display: 'flex'
            }}
            withBorder
          >
            <AppStack style={{ gap: 0 }}>
              <AppFlexbox
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                  Variant designs
                </AppText>
                <ActionIcon
                  color="dark"
                  onClick={() => {
                    setFormState((c) => ({
                      ...c,
                      showVariantSelect: !c.showVariantSelect
                    }));
                  }}
                  variant="subtle"
                >
                  <X size={20} />
                </ActionIcon>
              </AppFlexbox>
              <Divider />

              {!productDesign ? (
                <AppStack style={{ padding: 8, gap: 16 }}>
                  <AppText
                    style={{ fontSize: 12, color: '#666', textAlign: 'center' }}
                  >
                    To get started, select the product you want to design and
                    create a main design for it
                  </AppText>
                </AppStack>
              ) : (
                <AppStack style={{ padding: 8, gap: 16 }}>
                  <AppText
                    style={{ fontSize: 12, color: '#666', textAlign: 'center' }}
                  >
                    Select the variants you want to design
                  </AppText>

                  {selectedVariantDesign?.isMainDesign &&
                    productDesign.removedVariantIds?.length > 0 && (
                      <Alert
                        color="#C40000"
                        style={{ padding: 8 }}
                        variant="outline"
                      >
                        <AppStack style={{ gap: 0 }}>
                          <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                            <AlertCircle color="#C40000" size={16} />

                            <AppText style={{ fontSize: 14, color: '#C40000' }}>
                              {singularPluralFormat(
                                productDesign.removedVariantIds.length,
                                'variant',
                                'variants'
                              )}{' '}
                              removed from product
                            </AppText>
                          </AppFlexbox>

                          {removedColorOptions.length > 0 && (
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              This includes{' '}
                              {singularPluralFormat(
                                removedColorOptions.length,
                                'color',
                                'colors'
                              )}
                            </AppText>
                          )}

                          <Anchor
                            onClick={onClearRemovedVariants}
                            style={{ fontSize: 14, color: 'dodgerblue' }}
                          >
                            Re-add variants
                          </Anchor>
                        </AppStack>
                      </Alert>
                    )}

                  <AppStack style={{ gap: 8 }}>
                    <AppFlexbox
                      style={{
                        gap: 0,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                        Variants
                      </AppText>

                      <Button
                        disabled={loading || !selectedVariantDesign}
                        onClick={() => {
                          setVariantControlsState((c) => ({
                            ...c,
                            showVariantSelect: true,
                            selectedVariantDesign
                          }));
                        }}
                        size="compact-sm"
                        variant="outline"
                      >
                        Select variants
                      </Button>
                    </AppFlexbox>

                    <AppText style={{ fontSize: 14 }}>Colors</AppText>

                    <AppStack>
                      {selectedDesignColors.length === 0 ? (
                        <AppText
                          style={{
                            fontSize: 14,
                            color: '#666',
                            fontStyle: 'italic',
                            textAlign: 'center'
                          }}
                        >
                          No colors available for this design
                        </AppText>
                      ) : allVariantsHaveColorsSet ? (
                        <AppFlexbox
                          style={{
                            flex: 1,
                            width: '100%',
                            gap: 8,
                            flexWrap: 'wrap'
                          }}
                        >
                          {selectedDesignColors.map((v) => (
                            <WsColorSwatch
                              key={v.value}
                              onClick={() => {
                                setFormState((c) => ({
                                  ...c,
                                  selectedColor: {
                                    value: v.value,
                                    primaryColor: v.primaryColor,
                                    secondaryColor: v.secondaryColor
                                  }
                                }));
                                if (!formState.disableTemplateBackgroundColor) {
                                  changeBackgroundColor(
                                    v.primaryColor || '#FFF'
                                  );
                                }
                              }}
                              primaryColor={v.primaryColor}
                              secondaryColor={v.secondaryColor}
                              selected={
                                formState.selectedColor?.value === v.value
                              }
                              size={24}
                              value={v.value}
                            />
                          ))}
                        </AppFlexbox>
                      ) : (
                        <Select
                          data={selectedDesignColors.map((c) => ({
                            value: c.value,
                            label: c.value
                          }))}
                          onChange={(value) => {
                            const color = selectedDesignColors.find(
                              (c) => c.value === value
                            );
                            if (color) {
                              setFormState((c) => ({
                                ...c,
                                selectedColor: color
                              }));
                              if (!formState.disableTemplateBackgroundColor) {
                                changeBackgroundColor(
                                  color.primaryColor || '#FFF'
                                );
                              }
                            }
                          }}
                          placeholder="Select color"
                          size="sm"
                          value={formState.selectedColor?.value || null}
                        />
                      )}
                    </AppStack>

                    {formState.selectedColor &&
                      selectedDesignColors.some(
                        (c) => c.value === formState.selectedColor.value
                      ) &&
                      selectedVariantDesign?.isMainDesign && (
                        <Anchor
                          onClick={() => {
                            const colorVariantIds = ecomExternalProduct.ecomVendorProductVariants
                              .filter((variant) =>
                                variant.ecomVendorProductVariantOptions.some(
                                  (option) =>
                                    option.name === colorOption.name &&
                                    option.value ===
                                      formState.selectedColor.value
                                )
                              )
                              .map((v) => v.uuid)
                              .filter(
                                (variantId) =>
                                  !productDesign?.variantDesigns?.some((vd) =>
                                    vd.variantIds.includes(variantId)
                                  )
                              );
                            onAddVariantDesign(colorVariantIds);
                          }}
                          style={{
                            marginTop: 4,
                            color: 'dodgerblue',
                            fontSize: 14,
                            textAlign: 'center'
                          }}
                        >
                          Create a specific design for{' '}
                          {formState.selectedColor.value}
                        </Anchor>
                      )}
                  </AppStack>

                  <Divider />

                  <AppStack style={{ gap: 8 }}>
                    <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                      Designs
                    </AppText>

                    {productDesign?.variantDesigns
                      ?.sort((a, b) => (a.isMainDesign ? -1 : 1))
                      .map((vd, index) => {
                        const selected = selectedVariantDesign?.id === vd.id;

                        const colorOptionValuesForDesign = getSelectedDesignColors(
                          {
                            selectedVariantDesign: vd,
                            colorOption,
                            productDesign,
                            ecomExternalProduct
                          }
                        );

                        return (
                          <React.Fragment key={vd.id}>
                            <AppCard
                              key={vd.id}
                              className={
                                selected || !loading
                                  ? classes['hover-action-card']
                                  : null
                              }
                              onClick={() => {
                                if (!loading) {
                                  setFormState((c) => ({
                                    ...c,
                                    selectedVariantDesignId: vd.id
                                  }));
                                }
                              }}
                              shadow="none"
                              style={{
                                cursor:
                                  !selected && loading
                                    ? 'not-allowed'
                                    : 'pointer',
                                backgroundColor: selected ? '#E0E0E0' : '#FFF',
                                padding: 12
                              }}
                              withBorder
                            >
                              <AppFlexbox
                                style={{
                                  gap: 8,
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                {vd.isMainDesign ? (
                                  <AppStack style={{ gap: 0, flex: 1 }}>
                                    <AppText
                                      style={{ fontSize: 14, fontWeight: 500 }}
                                    >
                                      Main design
                                    </AppText>
                                    {productDesign.removedVariantIds?.length >
                                      0 && (
                                      <AppFlexbox
                                        style={{ gap: 5, alignItems: 'center' }}
                                      >
                                        <AlertCircle
                                          color="#C40000"
                                          size={16}
                                        />
                                        <AppText
                                          style={{
                                            fontSize: 14,
                                            color: '#C40000'
                                          }}
                                        >
                                          {singularPluralFormat(
                                            productDesign.removedVariantIds
                                              .length,
                                            'variant',
                                            'variants'
                                          )}{' '}
                                          removed
                                        </AppText>
                                      </AppFlexbox>
                                    )}
                                  </AppStack>
                                ) : (
                                  <AppStack style={{ gap: 0, flex: 1 }}>
                                    <AppText
                                      style={{ fontSize: 14, fontWeight: 500 }}
                                    >
                                      Extra design {index}
                                    </AppText>
                                    <AppText
                                      style={{ fontSize: 14, color: '#666' }}
                                    >
                                      {singularPluralFormat(
                                        vd.variantIds.length,
                                        'variant',
                                        'variants'
                                      )}
                                    </AppText>
                                    {allVariantsHaveColorsSet ? (
                                      <AppFlexbox style={{ gap: 4 }}>
                                        {colorOptionValuesForDesign.map((v) => (
                                          <WsColorSwatch
                                            key={v.value}
                                            primaryColor={v.primaryColor}
                                            secondaryColor={v.secondaryColor}
                                            size={14}
                                            value={v.value}
                                          />
                                        ))}
                                      </AppFlexbox>
                                    ) : (
                                      <AppText
                                        style={{ fontSize: 14, color: '#666' }}
                                      >
                                        {singularPluralFormat(
                                          colorOptionValuesForDesign.length,
                                          'color',
                                          'colors'
                                        )}
                                      </AppText>
                                    )}
                                  </AppStack>
                                )}

                                {!selected && !vd.isMainDesign && (
                                  <ActionIcon
                                    color="dark"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVariantControlsState((c) => ({
                                        ...c,
                                        showVariantRemove: true,
                                        selectedVariantDesign: vd
                                      }));
                                    }}
                                    variant="subtle"
                                  >
                                    <Trash color="#C40000" size={16} />
                                  </ActionIcon>
                                )}

                                {selected && (
                                  <AppStack>
                                    <Check color="green" size={24} />
                                  </AppStack>
                                )}
                              </AppFlexbox>
                            </AppCard>

                            {vd.isMainDesign &&
                              productDesign.variantDesigns.length > 1 && (
                                <Divider
                                  label="Extra designs"
                                  position="center"
                                />
                              )}
                          </React.Fragment>
                        );
                      })}

                    <AppFlexbox
                      onClick={() => {
                        setVariantControlsState((c) => ({
                          ...c,
                          showVariantSelect: true,
                          selectedVariantDesign: null
                        }));
                      }}
                      style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 5
                      }}
                    >
                      <CirclePlus color="dodgerblue" size={16} />
                      <Anchor
                        color="dodgerblue"
                        onClick={() => {}}
                        style={{
                          fontSize: 14,
                          textAlign: 'center'
                        }}
                      >
                        Add variant design
                      </Anchor>
                    </AppFlexbox>
                  </AppStack>
                </AppStack>
              )}
            </AppStack>
          </AppCard>
        </AppStack>

        <PrintDesignEditorVariantSelectModal
          ecomExternalProduct={ecomExternalProduct}
          isOpen={variantControlsState.showVariantSelect}
          onClose={() =>
            setVariantControlsState((c) => ({ ...c, showVariantSelect: false }))
          }
          onConfirm={(variantIds) => {
            if (variantControlsState.selectedVariantDesign) {
              onSelectDesignVariants(variantIds);
            }
            else {
              onAddVariantDesign(variantIds);
            }
          }}
          removedVariantIds={productDesign?.removedVariantIds || []}
          selectedVariantDesign={variantControlsState.selectedVariantDesign}
          variantDesigns={productDesign?.variantDesigns || []}
        />

        <ConfirmModal
          isOpen={variantControlsState.showVariantRemove}
          message="Are you sure you want to remove this variant design?"
          onCancel={() => {
            setVariantControlsState((c) => ({
              ...c,
              showVariantRemove: false
            }));
          }}
          onConfirm={() => {
            onRemoveVariantDesign(
              variantControlsState.selectedVariantDesign.id
            );
            setVariantControlsState((c) => ({
              ...c,
              showVariantRemove: false
            }));
          }}
          title="Remove variant design?"
        />
      </AppStack>
    )
  );
};

PrintDesignEditorVariantDesignControls.propTypes = {
  canvasController: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  printTemplateState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  ecomExternalProduct: PropTypes.object,
  loading: PropTypes.bool,
  printTemplate: PropTypes.object
};

export default PrintDesignEditorVariantDesignControls;
